import { useState, useMemo, useCallback } from 'react'
import { useDashReport } from '@/modules/dash/hooks/useDashReport'

interface FarmaciaItem {
  nombre: string
  total: number
  porcentaje: number
}

interface ProvinciaItem {
  nombre: string
  total: number
  porcentaje: number
}

interface RegistroItem {
  nombre: string
  provincia: string
  farmacia: string
}

interface OdfFarmacias0126Data {
  titulo: string
  subtitulo: string
  total_puntos: number
  total_farmacias: number
  total_provincias: number
  farmacias: FarmaciaItem[]
  provincias: ProvinciaItem[]
  google_sheets_url: string
  registros: RegistroItem[]
}

export function OdfFarmacias0126Page() {
  const { data, isLoading, error } = useDashReport<OdfFarmacias0126Data>('odf/farmacias-0126')
  const [selectedFarmacia, setSelectedFarmacia] = useState('todas')
  const [selectedProvincia, setSelectedProvincia] = useState('todas')
  const [farmaciasOpen, setFarmaciasOpen] = useState(true)
  const [provinciasOpen, setProvinciasOpen] = useState(false)

  // Compute filtered data
  const filteredData = useMemo(() => {
    if (!data) return null

    const registros = data.registros ?? []
    let filtrados = registros

    if (selectedFarmacia !== 'todas') {
      filtrados = filtrados.filter(r => r.farmacia === selectedFarmacia)
    }
    if (selectedProvincia !== 'todas') {
      filtrados = filtrados.filter(r => r.provincia === selectedProvincia)
    }

    const totalFiltrado = filtrados.length

    const farmaciasTotales: Record<string, number> = {}
    const provinciasTotales: Record<string, number> = {}
    filtrados.forEach(r => {
      farmaciasTotales[r.farmacia] = (farmaciasTotales[r.farmacia] || 0) + 1
      provinciasTotales[r.provincia] = (provinciasTotales[r.provincia] || 0) + 1
    })

    const farmacias = Object.entries(farmaciasTotales)
      .sort((a, b) => b[1] - a[1])
      .map(([nombre, total]) => ({
        nombre,
        total,
        porcentaje: totalFiltrado > 0 ? ((total / totalFiltrado) * 100).toFixed(2) : '0',
      }))

    const provincias = Object.entries(provinciasTotales)
      .sort((a, b) => b[1] - a[1])
      .map(([nombre, total]) => ({
        nombre,
        total,
        porcentaje: totalFiltrado > 0 ? ((total / totalFiltrado) * 100).toFixed(2) : '0',
      }))

    // Titulo
    let titulo = 'Resumen General'
    if (selectedFarmacia !== 'todas' && selectedProvincia !== 'todas') {
      titulo = `${selectedFarmacia} - ${selectedProvincia}`
    } else if (selectedFarmacia !== 'todas') {
      titulo = selectedFarmacia
    } else if (selectedProvincia !== 'todas') {
      titulo = selectedProvincia
    }

    return {
      totalFiltrado,
      totalFarmacias: Object.keys(farmaciasTotales).length,
      totalProvincias: Object.keys(provinciasTotales).length,
      farmacias,
      provincias,
      titulo,
    }
  }, [data, selectedFarmacia, selectedProvincia])

  // Compute available options for provincia dropdown (filtered by selected farmacia)
  const provinciaOptions = useMemo(() => {
    if (!data) return []
    let registros = data.registros ?? []
    if (selectedFarmacia !== 'todas') {
      registros = registros.filter(r => r.farmacia === selectedFarmacia)
    }
    const totales: Record<string, number> = {}
    registros.forEach(r => {
      totales[r.provincia] = (totales[r.provincia] || 0) + 1
    })
    return Object.entries(totales)
      .sort((a, b) => b[1] - a[1])
      .map(([nombre, total]) => ({ nombre, total }))
  }, [data, selectedFarmacia])

  const handleFarmaciaChange = useCallback((value: string) => {
    setSelectedFarmacia(value)
    // Update provincia selection if it's no longer available
    if (value !== 'todas') {
      setProvinciasOpen(true)
    }
  }, [])

  const handleProvinciaChange = useCallback((value: string) => {
    setSelectedProvincia(value)
  }, [])

  const limpiarFiltros = useCallback(() => {
    setSelectedFarmacia('todas')
    setSelectedProvincia('todas')
  }, [])

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data || !filteredData) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">{data.titulo}</h1>
                <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a href="https://enterprise.tonalstrategies.com/pricing/" target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm">
                Calculador
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              {data.google_sheets_url && data.google_sheets_url !== '#' && (
                <a href={data.google_sheets_url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 2h8v2H6V6zm0 4h8v2H6v-2zm0 4h4v2H6v-2z"/>
                  </svg>
                  Ver Datos Fuente
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col gap-5">
            {/* Filtro por Farmacia (botones pill) */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center shrink-0">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="font-semibold text-gray-900 text-sm">Filtrar por farmacia:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleFarmaciaChange('todas')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedFarmacia === 'todas'
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Todas
                </button>
                {data.farmacias.map(f => (
                  <button
                    key={f.nombre}
                    onClick={() => handleFarmaciaChange(f.nombre)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      selectedFarmacia === f.nombre
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {f.nombre}
                  </button>
                ))}
              </div>
            </div>

            {/* Filtro por Provincia (select) */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center shrink-0">
                <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="font-semibold text-gray-900 text-sm">Filtrar por provincia:</span>
              </div>
              <select
                value={selectedProvincia}
                onChange={(e) => handleProvinciaChange(e.target.value)}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              >
                <option value="todas">Todas las provincias</option>
                {provinciaOptions.map(p => (
                  <option key={p.nombre} value={p.nombre}>{p.nombre} ({p.total})</option>
                ))}
              </select>
            </div>

            {/* Limpiar */}
            <div className="flex justify-end">
              <button
                onClick={limpiarFiltros}
                className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors duration-200 border border-gray-300"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Limpiar Filtros
              </button>
            </div>
          </div>
        </div>

        {/* Estadisticas Generales */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 lg:p-6 border border-green-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span>{filteredData.titulo}</span>
          </h2>

          {/* Totales */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-900">{filteredData.totalFiltrado.toLocaleString('es-EC')}</p>
              <p className="text-sm text-gray-600 mt-1">Total Farmaceutas</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-green-600">{filteredData.totalFarmacias}</p>
              <p className="text-sm text-gray-600 mt-1">Cadenas de Farmacias</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-blue-600">{filteredData.totalProvincias}</p>
              <p className="text-sm text-gray-600 mt-1">Provincias</p>
            </div>
          </div>
        </div>

        {/* Desglose por Farmacia */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setFarmaciasOpen(!farmaciasOpen)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-green-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span className="text-base lg:text-lg">Desglose por Cadena de Farmacia</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">({filteredData.totalFarmacias} cadena{filteredData.totalFarmacias !== 1 ? 's' : ''})</span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${farmaciasOpen ? 'rotate-180' : ''}`}
                style={!farmaciasOpen ? { animation: 'pulse-subtle 2s ease-in-out infinite' } : undefined}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {farmaciasOpen && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full bg-white text-xs lg:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">Farmacia</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Farmaceutas</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">%</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Distribucion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredData.farmacias.map(farm => (
                    <tr key={farm.nombre} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{farm.nombre}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{farm.total}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-green-600">{farm.porcentaje}%</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div className="bg-green-600 h-2.5 rounded-full" style={{ width: `${farm.porcentaje}%` }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-bold text-gray-900">TOTAL</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-gray-900">{filteredData.totalFiltrado}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-green-600">100%</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Desglose por Provincia */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setProvinciasOpen(!provinciasOpen)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-blue-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-base lg:text-lg">Desglose por Provincia</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">({filteredData.totalProvincias} provincia{filteredData.totalProvincias !== 1 ? 's' : ''})</span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${provinciasOpen ? 'rotate-180' : ''}`}
                style={!provinciasOpen ? { animation: 'pulse-subtle 2s ease-in-out infinite' } : undefined}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          {provinciasOpen && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full bg-white text-xs lg:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">Provincia</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Farmaceutas</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">%</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Distribucion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredData.provincias.map(prov => (
                    <tr key={prov.nombre} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{prov.nombre}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{prov.total}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-blue-600">{prov.porcentaje}%</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${prov.porcentaje}%` }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-bold text-gray-900">TOTAL</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-gray-900">{filteredData.totalFiltrado}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-blue-600">100%</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 text-center text-gray-600 text-xs lg:text-sm">
          <p>&copy; 2026 Tonal Strategies - Analisis de Farmaceutas</p>
        </div>
      </div>

      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.1); opacity: 1; }
        }
      `}</style>
    </div>
  )
}
